import React from 'react';
import { Asset, Button, WarningIcon } from '@clearcover/atlas';
import {
  VehicleInspectionRequest as VehicleInspectionRequestType,
  VehicleInspectionDetails as VehicleInspectionDetailsType,
} from '@clearcover/core-app-types';
import cx from 'classnames';
import { Asset as SharedAsset } from 'Shared/components';
import { useAuth0 } from 'Auth';
import { editVehicleInspectionPolicy } from 'Admin/authPolicies';
import { ChangeStatusType } from '../types';
import { ViewMediaComponent } from '../ViewMediaComponent';
import { enumToText } from '../VehicleInspectionRequest';

import styles from './styles.module.css';

interface VehicleInspectionRequestDetailsProps {
  requestId: string;
  details: Array<VehicleInspectionDetailsType>;
  currentStatus: VehicleInspectionRequestType['status'];
  requiresRemediation: VehicleInspectionRequestType['requiresRemediation'];
  onChangeStatus: ChangeStatusType;
  onRemediate: () => void;
}

export const VehicleInspectionRequestDetails: React.FC<
  VehicleInspectionRequestDetailsProps
> = ({
  requestId,
  details,
  currentStatus,
  requiresRemediation,
  onChangeStatus,
  onRemediate,
}) => {
  const { user } = useAuth0();

  const VALID_STATUSES: Array<VehicleInspectionRequestType['status']> = [
    'OVERDUE',
    'SUBMITTED',
    'ESCALATE',
  ];

  const canChangeStatus = editVehicleInspectionPolicy(user!.effectiveGrants);

  return (
    <ul className={styles.detailsTable}>
      <li key="title" className={styles.headers}>
        <span className={styles.header}>Status</span>
        <span className={styles.header}>Type</span>
        <span className={styles.header}>Date Requested</span>
        <span className={styles.header}>Due Date</span>
        <span className={styles.header}>Date Provided</span>
        <span className={styles.header}>Location</span>
        <span className={styles.header}>Media</span>
        <span className={styles.header}></span>
      </li>

      {[...details]
        .sort((a, b) => (a.requestDate > b.requestDate ? -1 : 1))
        .map((detail, index) => {
          return (
            <li key={index} className={styles.row}>
              <div
                className={cx(styles.data, {
                  [styles.submitted]: detail.detailStatus === 'SUBMITTED',
                  [styles.withIcon]: detail.detailStatus === 'OVERDUE',
                })}
              >
                {detail.detailStatus === 'OVERDUE' && (
                  <Asset
                    icon={WarningIcon}
                    description="Warning"
                    height={16}
                    width={16}
                    className={styles.warningIcon}
                  />
                )}
                {enumToText(detail.detailStatus, 'STATUS')}
              </div>
              <div className={styles.data}>{detail.stage}</div>
              <div className={styles.data}>{detail.requestDate}</div>
              <div className={styles.data}>{detail.expirationDate}</div>
              <div className={styles.data}>{detail.submittedDate}</div>
              <div className={styles.data}>
                {detail.location && (
                  <a
                    href={`https://maps.google.com/?q=${detail.location.latitude},${detail.location.longitude}`}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.link}
                  >
                    View map
                    <SharedAsset variant="openInNew" />
                  </a>
                )}
              </div>
              <div className={styles.data}>
                <ViewMediaComponent
                  artifactId={detail.artifactId}
                  requestId={requestId}
                  onChangeStatus={onChangeStatus}
                  currentStatus={currentStatus}
                />
              </div>
              <div className={styles.data}>
                {details.length - 1 === index &&
                  VALID_STATUSES.includes(currentStatus) &&
                  canChangeStatus &&
                  requiresRemediation && (
                    <Button
                      variant="danger"
                      size="sm"
                      className={styles.button}
                      onClick={onRemediate}
                    >
                      Overdue&nbsp;process
                    </Button>
                  )}
              </div>
            </li>
          );
        })}
    </ul>
  );
};
